<template>
  <div>
    <h1 class="h1">{{ title }}</h1>
    <h4 class="h4" v-if="!hasUserAnInvitation">{{ onboardingTextConfig.onboardingSecondFinalSubtitle }}</h4>
    <h4 class="h4" v-else>{{ invitedUserCompletedRegistrationMessage }}</h4>

    <div style="display: flex; justify-content: center; margin-top: 1rem;">
      <ds-button
        v-if="hasUserAnInvitation || needsRegistrationSubmission"
        type="submit"
        variant="rounded"
        style="max-width: 300px;"
        :label="$t('onboarding_finish_cta')"
        :disabled="redirecting"
        :loading="redirecting"
        @click.prevent="submit"
      />
    </div>

    <div class="onboarding-invited-by" v-if="invitedBy">
      {{ $t('onboarding_invited_by') }} {{ invitedBy }}
    </div>
  </div>
</template>

<script lang="ts">
  import OnboardingMixin from '../../util/OnboardingMixin.js'
  import { trackHeapEvent } from '../../util/analytics.js'
  import { registerRequest, loginRequest } from '../../api/user.js'
  import { MUTATION_TYPES as USER_MUTATION_TYPES } from '../../store/modules/user-helpers.js'
  import { ACTION_TYPES as LOCALIZATION_ACTION_TYPES } from '../../store/modules/localization.js'
  import { defineComponent } from '~/vue'

  export default defineComponent({
    props: {
      registrationData: Object,
    },
    data() {
      return {
        invitedBy: window.invited_by,
        redirecting: false,
      }
    },
    computed: {
      hasUserAnInvitation(): boolean {
        return !!this.$route?.query.invitation
      },
      title(): string {
        if (this.hasUserAnInvitation) {
          return this.onboardingTextConfig.onboardingRegistrationFinalisedThroughInvitationTitle
        }

        return this.onboardingTextConfig.onboardingSecondFinalTitle
      },
      invitedUserCompletedRegistrationMessage(): string {
        return this.$t('invited_user_completed_registration_message') + ' ' + this.$store.getters.ecosystemDisplayName + '.'
      },
      needsRegistrationSubmission(): boolean {
        // True when registration was deferred to this final step (e.g. Klik community-selection flow).
        // OnboardingActorSignUp-based flows clear registrationData before reaching this step,
        // so this returns false and no duplicate POST is made.
        if (this.hasUserAnInvitation) return false
        return !!this.registrationData && Object.keys(this.registrationData).length > 0
      },
      isKlikEcosystem(): boolean {
        return this.$store.getters.isKlik
      },
    },
    methods: {
      async submit() {
        this.redirecting = true

        // Handle registration submission for public registrations
        if (this.needsRegistrationSubmission) {
          try {
            const response = await registerRequest(this.registrationData)

            if (response.success) {
              trackHeapEvent('onboarding-registration-complete')

              // If user is verified, log them in
              if (response.invitationCompleted) {
                const loginResponse = await loginRequest({
                  email: this.registrationData.email,
                  password: this.registrationData.password,
                  // Not remembered: the visitor never asked for a 400-day
                  // `remember_web_*` cookie, and there is no checkbox here to
                  // ask them. They can opt in on the login form.
                  remember: false,
                  locale: this.$store.getters.activeLocale,
                })

                if (loginResponse && loginResponse.id) {
                  this.$store.commit(USER_MUTATION_TYPES.STORE_PROFILE, loginResponse)
                  this.$store.dispatch(LOCALIZATION_ACTION_TYPES.UPDATE_ACTIVE_LOCALE, {
                    locale: this.$store.getters.activeLocale,
                    i18n: this.$i18n
                  })

                  this.$emit('submitted-final-step')
                }
              }

              // Handle redirect
              if (this.$route.query.redirect_uri) {
                let redirectUri = decodeURIComponent(this.$route.query.redirect_uri)
                delete this.$route.query.redirect_uri
                await this.$router.push(redirectUri + '?' + new URLSearchParams(this.$route.query).toString())
              } else {
                await this.$router.push(response.invitationCompleted ? '/' : '/profile/update')
              }

              window.location.reload()
            }
          } catch (error) {
            console.error('Registration failed:', error)
            this.redirecting = false
            // Handle error - could emit an error event or show error message
          }
        } else {
          // Handle invitation flow
          if (this.$route.query.redirect_uri) {
            let redirectUri = decodeURIComponent(this.$route.query.redirect_uri)
            delete this.$route.query.redirect_uri
            await this.$router.push(redirectUri + '?' + new URLSearchParams(this.$route.query).toString())
          } else {
            await this.$router.push('/profile/update')
          }

          trackHeapEvent('onboarding-registration-complete')
          window.location.reload()
        }
      },
    },
    mixins: [OnboardingMixin],
  })
</script>
